import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Lock } from "lucide-react";
import { MEDALS } from "../lib/medals";
import { MEDAL_IMAGES } from "../lib/constants";
import { ProfileBar } from "../components/ProfileBar";
import { PageShell } from "../components/PageShell";
import { ClayButton } from "../components/ClayButton";

const TIER_COLOR = {
  gold: "#FFD700",
  silver: "#C0C0C0",
  bronze: "#CD7F32",
};

export default function Medallas({ profile, onReset }) {
  const navigate = useNavigate();
  const unlocked = new Set(profile.medals || []);

  return (
    <PageShell>
      <ProfileBar profile={profile} onReset={onReset} />

      <div className="mt-6">
        <ClayButton variant="ghost" onClick={() => navigate("/dashboard")} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" strokeWidth={3} />
          Volver
        </ClayButton>
      </div>

      <div className="mt-2 mb-6 sm:mb-8">
        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1C1C1E] mb-2">
          Mis Medallas
        </h2>
        <p className="text-base sm:text-lg text-[#5C5C60]">
          Has desbloqueado <strong>{unlocked.size}</strong> de <strong>{MEDALS.length}</strong>{" "}
          medallas. ¡Sigue practicando!
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="medals-grid">
        {MEDALS.map((m, i) => {
          const isUnlocked = unlocked.has(m.id);
          const tierColor = TIER_COLOR[m.tier];
          return (
            <motion.div
              key={m.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04, type: "spring", bounce: 0.3 }}
              className={`relative rounded-[2rem] p-5 text-center border-4 ${
                isUnlocked
                  ? "bg-white border-white shadow-[0_12px_25px_-12px_rgba(0,0,0,0.15)]"
                  : "bg-gray-50 border-gray-100"
              }`}
              data-testid={`medal-${m.id}${isUnlocked ? "-unlocked" : "-locked"}`}
            >
              <div className="relative mx-auto w-20 h-20 sm:w-24 sm:h-24 mb-3">
                <img
                  src={MEDAL_IMAGES[m.tier]}
                  alt={m.name}
                  className={`w-full h-full object-contain ${
                    isUnlocked ? "" : "grayscale opacity-30"
                  }`}
                />
                {!isUnlocked && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-10 h-10 rounded-full bg-white shadow flex items-center justify-center">
                      <Lock className="w-5 h-5 text-gray-400" strokeWidth={3} />
                    </div>
                  </div>
                )}
              </div>
              <h3
                className={`font-heading text-base sm:text-lg font-bold mb-1 ${
                  isUnlocked ? "text-[#1C1C1E]" : "text-gray-400"
                }`}
              >
                {m.name}
              </h3>
              <p className={`text-xs ${isUnlocked ? "text-[#5C5C60]" : "text-gray-400"}`}>
                {m.description}
              </p>
              {isUnlocked && (
                <span
                  className="inline-block mt-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: tierColor + "33", color: "#1C1C1E" }}
                >
                  {m.tier === "gold" ? "Oro" : m.tier === "silver" ? "Plata" : "Bronce"}
                </span>
              )}
            </motion.div>
          );
        })}
      </div>
    </PageShell>
  );
}
